import { type StaffProfileAnswers } from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

/**
 * The staff read of a member's answers (P27c1) — `GET /admin/users/:id/answers`,
 * the N18 exception on `members.read`, audited per read by the api.
 *
 * A W6 bridge rather than a module api because two modules draw it: the
 * members detail (its Profile tab) and the verification case, where a reviewer
 * of a FIRST look needs the name beside the passport. Neither may import the
 * other's api, and the kit panel that renders it may import neither.
 *
 * Nothing invalidates it from the bus: staff never edit a member's answers,
 * and the member's own edits reach the portal through a re-review case, not
 * an event. The cache expires on RTK's own clock, and every mount of the panel
 * is a fresh audited read — which is what an audited read should be.
 */
export const memberAnswersLookupApi = createApi({
  reducerPath: 'memberAnswersLookupApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['MemberAnswers'],
  // Read again on every mount: the audit trail is the point of this route, and
  // a panel painted from a minute-old cache is a read nobody recorded.
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    memberAnswers: builder.query<StaffProfileAnswers, string>({
      query: (userId) => ({ url: `/admin/users/${userId}/answers`, method: 'GET' }),
      providesTags: (_r, _e, userId) => [{ type: 'MemberAnswers' as const, id: userId }],
    }),
  }),
});

export const { useMemberAnswersQuery } = memberAnswersLookupApi;
